import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { SendInvitationHistoryEntry } from '@app/v2/event/types/invitation-tracker.type'
import {
  INVITATION_CHANNEL_MOBILE_EMPTY_ICON_SRC,
  INVITATION_CHANNEL_POPOVER_EMPTY_ICON_SRC,
  invitationChannelEmptyCopy,
  invitationHistoryEntryTrackKey,
  invitationHistoryStatusBadgeClass,
  invitationHistoryStatusBadgeClassDark,
  invitationHistoryStatusDotClass,
  shouldLoadMoreInvitationHistoryOnScroll
} from '@app/v2/event/utils/invitation-channel-ui.util'
import { InvitationChannel, formatInvitationHistoryStatusLabel } from '@app/v2/event/utils/invitation-channel-status.util'
import { formatInvitationHistoryDate } from '@app/v2/event/utils/invitation-history-date.util'
import { InvitationChannelHistorySkeletonComponent } from '../invitation-channel-history-skeleton/invitation-channel-history-skeleton.component'

export type InvitationChannelHistoryBodyVariant = 'dark' | 'light'

@Component({
  selector: 'app-invitation-channel-history-body',
  standalone: true,
  imports: [NgClass, InvitationChannelHistorySkeletonComponent],
  templateUrl: './invitation-channel-history-body.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tw-flex tw-w-full tw-flex-col'
  }
})
export class InvitationChannelHistoryBodyComponent {
  public readonly channel = input.required<InvitationChannel>()
  public readonly entries = input<SendInvitationHistoryEntry[]>([])
  public readonly isLoading = input(false)
  public readonly isLoadingMore = input(false)
  public readonly hasMore = input(false)
  public readonly variant = input<InvitationChannelHistoryBodyVariant>('light')
  public readonly maxHeightClass = input('tw-max-h-[250px]')

  public readonly loadMore = output<void>()

  public readonly isEmpty = computed(() => this.entries().length <= 0)

  public readonly emptyCopy = computed(() => invitationChannelEmptyCopy(this.channel()))

  public readonly emptyIconSrc = computed(() =>
    this.variant() === 'dark' ? INVITATION_CHANNEL_POPOVER_EMPTY_ICON_SRC[this.channel()] : INVITATION_CHANNEL_MOBILE_EMPTY_ICON_SRC[this.channel()]
  )

  public readonly entryTrackKey = invitationHistoryEntryTrackKey

  public readonly formatHistoryStatus = formatInvitationHistoryStatusLabel

  /**
   * Status dot classes for a history row invitation status.
   */
  public statusDotClass(status: string): string {
    return invitationHistoryStatusDotClass(status, this.channel())
  }

  /**
   * Status badge classes for the active surface variant.
   */
  public badgeClass(status: string): string {
    return this.variant() === 'dark'
      ? invitationHistoryStatusBadgeClassDark(status, this.channel())
      : invitationHistoryStatusBadgeClass(status, this.channel())
  }

  /**
   * Formats a history row `created_at` for display.
   */
  public formatHistoryDate(createdAt: string): string {
    return formatInvitationHistoryDate(createdAt)
  }

  /**
   * Emits load-more when scrolled near the bottom and another page is available.
   * @param event {Event} - The scroll event.
   */
  public onHistoryScroll(event: Event): void {
    if (!this.hasMore() || this.isLoadingMore() || this.isLoading()) return
    if (!shouldLoadMoreInvitationHistoryOnScroll(event)) return
    this.loadMore.emit()
  }
}
